"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var Enums_1 = require("../types/Enums");
var uuid_1 = require("uuid");
var MeasureBundleHelpers_1 = require("../helpers/MeasureBundleHelpers");
var CustomErrors_1 = require("../types/errors/CustomErrors");
var DetailedResultsHelpers_1 = require("../helpers/DetailedResultsHelpers");
var MeasureReportBuilder = /** @class */ (function () {
    function MeasureReportBuilder(measureBundle, options) {
        var _a, _b, _c;
        this.report = {
            id: uuid_1.v4(),
            resourceType: 'MeasureReport'
        };
        this.measureBundle = measureBundle;
        this.measure = MeasureBundleHelpers_1.extractMeasureFromBundle(measureBundle);
        this.scoringCode =
            ((_c = (_b = (_a = this.measure.scoring) === null || _a === void 0 ? void 0 : _a.coding) === null || _b === void 0 ? void 0 : _b.find(function (c) {
                return c.system === 'http://hl7.org/fhir/measure-scoring' ||
                    c.system === 'http://terminology.hl7.org/CodeSystem/measure-scoring';
            })) === null || _c === void 0 ? void 0 : _c.code) || '';
        this.options = options;
        // if report type is specified use it, otherwise default to individual report.
        if (this.options.reportType) {
            this.isIndividual = this.options.reportType === 'individual';
        }
        else {
            this.isIndividual = true;
        }
        // determine if we should be calculating SDE, TODO: Support SDEs for summary/subject-list.
        this.calculateSDEs = this.options.calculateSDEs === true && this.isIndividual;
        this.patientCount = 0;
        this.setupBasicStructure();
        this.setupPopulationGroups();
    }
    MeasureReportBuilder.prototype.setupBasicStructure = function () {
        // simple fields
        this.report.period = {
            start: this.options.measurementPeriodStart,
            end: this.options.measurementPeriodEnd
        };
        this.report.status = 'complete';
        this.report.type = this.isIndividual ? 'individual' : 'summary';
        // measure url from measure bundle
        this.report.measure = this.measure.url || 'UnknownMeasure'; // or some other default?
        this.report.contained = [];
        // add narrative if specified
        if (this.options.calculateHTML && this.isIndividual) {
            this.report.text = {
                status: 'generated',
                div: ''
            };
        }
    };
    MeasureReportBuilder.prototype.setupPopulationGroups = function () {
        var _this = this;
        var _a;
        // create group population group based on measure resource
        this.report.group = [];
        this.report.evaluatedResource = [];
        // build population groups from measure resource
        (_a = this.measure.group) === null || _a === void 0 ? void 0 : _a.forEach(function (measureGroup) {
            var _a, _b;
            var group = {};
            if (measureGroup.id) {
                group.id = measureGroup.id;
            }
            group.population = [];
            // build each population group with 0 for initial value
            (_a = measureGroup.population) === null || _a === void 0 ? void 0 : _a.forEach(function (measurePopulation) {
                var _a, _b, _c;
                var pop = {};
                pop.count = 0;
                // copy coding values for the population from the measure
                if ((_a = measurePopulation.code) === null || _a === void 0 ? void 0 : _a.coding) {
                    var popCoding = (_b = measurePopulation.code) === null || _b === void 0 ? void 0 : _b.coding[0];
                    pop.code = {
                        coding: [
                            {
                                system: popCoding.system,
                                code: popCoding.code,
                                display: popCoding.display
                            }
                        ]
                    };
                }
                (_c = group.population) === null || _c === void 0 ? void 0 : _c.push(pop);
            });
            // if the measure definition has stratification, add group.stratifier
            if (measureGroup.stratifier) {
                group.stratifier = [];
                measureGroup.stratifier.forEach(function (s) {
                    var _a;
                    var reportStratifier = {};
                    reportStratifier.code = s.code ? [s.code] : [];
                    var strat = {};
                    // use existing populations, but reduce count as appropriate
                    // Deep copy population with matching attributes but different interface
                    strat.population = (JSON.parse(JSON.stringify(group.population)));
                    reportStratifier.stratum = [strat];
                    (_a = group.stratifier) === null || _a === void 0 ? void 0 : _a.push(reportStratifier);
                });
            }
            (_b = _this.report.group) === null || _b === void 0 ? void 0 : _b.push(group);
        });
    };
    MeasureReportBuilder.prototype.addPatientResults = function (results) {
        var _this = this;
        // if this is a individual measure report and we have already received a patient we should throw
        // an error
        if (this.isIndividual && this.patientCount > 0) {
            throw new CustomErrors_1.UnsupportedProperty('The MeasureReport being built is an individual report and patient results have already been added');
        }
        if (!results.detailedResults) {
            throw new Error('ExecutionResults are missing detailedResults.');
        }
        // if we are creating an individual report create the patient reference.
        if (this.isIndividual) {
            var patId = "Patient/" + results.patientId;
            var subjectReference = {
                reference: patId
            };
            this.report.subject = subjectReference;
        }
        results.detailedResults.forEach(function (groupResults, i) {
            var _a, _b, _c, _d;
            if (_this.isIndividual) {
                // add narrative for relevant clauses
                if (DetailedResultsHelpers_1.isDetailedResult(groupResults) && _this.report.text && groupResults.html) {
                    _this.report.text.div += groupResults.html;
                }
            }
            // find corresponding group in report
            // default to index if group is missing an ID
            var group = ((_a = _this.report.group) === null || _a === void 0 ? void 0 : _a.find(function (g) { return g.id == groupResults.groupId; })) || ((_b = _this.report.group) === null || _b === void 0 ? void 0 : _b[i]);
            if (!group) {
                throw new CustomErrors_1.UnexpectedProperty("Group " + groupResults.groupId + " not found in measure report");
            }
            // iterate over population results for episodes and increment the counters
            // TODO: handle EXM111 (doesn't identify itself as a episode of care measure). if it's an episode of care, you need to iterate over
            // stratifications : may need to clone results for one population group and adjust (in this case, just a straight clone)
            if (groupResults === null || groupResults === void 0 ? void 0 : groupResults.episodeResults) {
                groupResults.episodeResults.forEach(function (er) {
                    var _a, _b;
                    (_a = er.populationResults) === null || _a === void 0 ? void 0 : _a.forEach(function (pr) {
                        _this.incrementPopulationInGroup(group, pr);
                    });
                    // TODO: update this when we support CV for summary/subject-list reports
                    if (_this.scoringCode === Enums_1.MeasureScoreType.CV) {
                        group.measureScore = _this.calcMeasureScoreCV(_this.measure, groupResults, group.id || '');
                        _this.addScoreObservation(group.measureScore, _this.measure, _this.report);
                    }
                    // add to stratifier results if there are any
                    if (group.stratifier) {
                        (_b = er.stratifierResults) === null || _b === void 0 ? void 0 : _b.forEach(function (stratResults) {
                            var _a, _b, _c;
                            // only add to results if this episode is in the strata
                            if (stratResults.result) {
                                var strata = (_a = group.stratifier) === null || _a === void 0 ? void 0 : _a.find(function (s) { return s.code && s.code[0].text === stratResults.strataCode; });
                                var stratum_1 = (_b = strata === null || strata === void 0 ? void 0 : strata.stratum) === null || _b === void 0 ? void 0 : _b[0];
                                if (stratum_1) {
                                    (_c = er.populationResults) === null || _c === void 0 ? void 0 : _c.forEach(function (pr) {
                                        _this.incrementPopulationInStratum(stratum_1, pr);
                                    });
                                    // TODO: update this when we support CV for summary/subject-list reports
                                    if (_this.scoringCode === Enums_1.MeasureScoreType.CV) {
                                        stratum_1.measureScore = _this.calcMeasureScoreCV(_this.measure, groupResults, group.id || '', stratResults.strataCode);
                                    }
                                }
                                else {
                                    throw new CustomErrors_1.UnexpectedProperty("Stratum " + stratResults.strataCode + " in group " + group.id + " not found in measure reports");
                                }
                            }
                        });
                    }
                });
            }
            else {
                (_c = groupResults.populationResults) === null || _c === void 0 ? void 0 : _c.forEach(function (pr) {
                    _this.incrementPopulationInGroup(group, pr);
                });
                // TODO: update this when we support CV for summary/subject-list reports
                if (_this.scoringCode === Enums_1.MeasureScoreType.CV) {
                    group.measureScore = _this.calcMeasureScoreCV(_this.measure, groupResults, group.id || '');
                    _this.addScoreObservation(group.measureScore, _this.measure, _this.report);
                }
                // add to stratifier results if there are any
                if (group.stratifier) {
                    (_d = groupResults.stratifierResults) === null || _d === void 0 ? void 0 : _d.forEach(function (stratResults) {
                        var _a, _b, _c;
                        // only add to results if this patient is in the strata
                        if (stratResults.result) {
                            var strata = (_a = group.stratifier) === null || _a === void 0 ? void 0 : _a.find(function (s) { return s.code && s.code[0].text === stratResults.strataCode; });
                            var stratum_2 = (_b = strata === null || strata === void 0 ? void 0 : strata.stratum) === null || _b === void 0 ? void 0 : _b[0];
                            if (stratum_2) {
                                (_c = groupResults.populationResults) === null || _c === void 0 ? void 0 : _c.forEach(function (pr) {
                                    _this.incrementPopulationInStratum(stratum_2, pr);
                                });
                                // TODO: update this when we support CV for summary/subject-list reports
                                if (_this.scoringCode === Enums_1.MeasureScoreType.CV) {
                                    stratum_2.measureScore = _this.calcMeasureScoreCV(_this.measure, groupResults, group.id || '', stratResults.strataCode);
                                }
                            }
                            else {
                                throw new CustomErrors_1.UnexpectedProperty("Stratum " + stratResults.strataCode + " in group " + group.id + " not found in measure reports");
                            }
                        }
                    });
                }
            }
        });
        if (this.calculateSDEs) {
            this.addSDE(results);
        }
        if (results.evaluatedResource) {
            results.evaluatedResource.forEach(function (resource) {
                var _a, _b;
                var reference = {
                    reference: resource.resourceType + "/" + resource.id
                };
                if (!((_a = _this.report.evaluatedResource) === null || _a === void 0 ? void 0 : _a.find(function (r) { return r.reference === reference.reference; }))) {
                    (_b = _this.report.evaluatedResource) === null || _b === void 0 ? void 0 : _b.push(reference);
                }
            });
        }
        this.patientCount++;
    };
    MeasureReportBuilder.prototype.incrementPopulationInStratum = function (stratum, pr) {
        var _a;
        var pop = (_a = stratum.population) === null || _a === void 0 ? void 0 : _a.find(function (pop) { var _a; return ((_a = pop.code) === null || _a === void 0 ? void 0 : _a.coding) && pop.code.coding[0].code === pr.populationType; });
        if (pop) {
            // add to pop count creating it if not already created.
            if (!pop.count)
                pop.count = 0;
            pop.count += pr.result ? 1 : 0;
        }
        else {
            throw new CustomErrors_1.UnexpectedProperty("Population " + pr.populationType + " in stratum " + stratum.id + " not found in measure report.");
        }
    };
    MeasureReportBuilder.prototype.incrementPopulationInGroup = function (group, pr) {
        var _a;
        var pop = (_a = group.population) === null || _a === void 0 ? void 0 : _a.find(function (pop) { var _a; return ((_a = pop.code) === null || _a === void 0 ? void 0 : _a.coding) && pop.code.coding[0].code === pr.populationType; });
        if (pop) {
            // add to pop count creating it if not already created.
            if (!pop.count)
                pop.count = 0;
            pop.count += pr.result ? 1 : 0;
        }
        else {
            throw new CustomErrors_1.UnexpectedProperty("Population " + pr.populationType + " in group " + group.id + " not found in measure report.");
        }
    };
    MeasureReportBuilder.prototype.addSDE = function (result) {
        var _this = this;
        var _a;
        // 	Note that supplemental data are reported as observations for each patient and included in the evaluatedResources bundle. See the MeasureReport resource or the Quality Reporting topic for more information.
        (_a = result.supplementalData) === null || _a === void 0 ? void 0 : _a.forEach(function (sd) {
            var _a, _b, _c, _d, _e;
            var observation = {};
            observation.resourceType = 'Observation';
            observation.code = { text: sd.name };
            observation.id = uuid_1.v4();
            observation.status = 'final';
            observation.extension = [
                {
                    url: 'http://hl7.org/fhir/StructureDefinition/cqf-measureInfo',
                    extension: [
                        {
                            url: 'measure',
                            valueCanonical: _this.measure.url
                        },
                        {
                            url: 'populationId',
                            valueString: sd.name
                        }
                    ]
                }
            ];
            // add coding to valueCodeableConcept
            if ('forEach' in sd.rawResult) {
                observation.valueCodeableConcept = { coding: [] };
                (_a = sd.rawResult) === null || _a === void 0 ? void 0 : _a.forEach(function (rr) {
                    var _a, _b, _c, _d, _e, _f, _g;
                    if (((_a = rr.code) === null || _a === void 0 ? void 0 : _a.value) && ((_b = rr.system) === null || _b === void 0 ? void 0 : _b.value) && ((_c = rr.display) === null || _c === void 0 ? void 0 : _c.value)) {
                        // create supplemental data elements
                        (_e = (_d = observation.valueCodeableConcept) === null || _d === void 0 ? void 0 : _d.coding) === null || _e === void 0 ? void 0 : _e.push({
                            system: rr.system.value,
                            code: rr.code.value,
                            display: rr.display.value
                        });
                    }
                    else if (rr.isCode) {
                        // if a CQL system code is returned
                        (_g = (_f = observation.valueCodeableConcept) === null || _f === void 0 ? void 0 : _f.coding) === null || _g === void 0 ? void 0 : _g.push({
                            system: rr.system,
                            code: rr.code,
                            display: rr.display
                        });
                    }
                });
            }
            else if (sd.rawResult.isCode) {
                observation.valueCodeableConcept = { coding: [] };
                (_c = (_b = observation.valueCodeableConcept) === null || _b === void 0 ? void 0 : _b.coding) === null || _c === void 0 ? void 0 : _c.push({
                    system: sd.rawResult.system,
                    code: sd.rawResult.code,
                    display: sd.rawResult.display
                });
            }
            // add as evaluated resource reference
            (_d = _this.report.contained) === null || _d === void 0 ? void 0 : _d.push(observation);
            (_e = _this.report.evaluatedResource) === null || _e === void 0 ? void 0 : _e.push({
                reference: "#" + observation.id
            });
        });
    };
    MeasureReportBuilder.prototype.populationTotal = function (population, type) {
        var _a;
        return (((_a = population === null || population === void 0 ? void 0 : population.find(function (pop) {
            var _a, _b;
            return ((_a = pop.code) === null || _a === void 0 ? void 0 : _a.coding) && pop.code.coding.length > 0 && ((_b = pop.code) === null || _b === void 0 ? void 0 : _b.coding[0].code) === type;
        })) === null || _a === void 0 ? void 0 : _a.count) || 0.0);
    };
    MeasureReportBuilder.prototype.median = function (observations) {
        var sorted = observations.sort(function (a, b) {
            return a - b;
        });
        var centerIndex = Math.floor(sorted.length / 2);
        if (sorted.length % 2 === 0) {
            return 0.5 * (sorted[centerIndex - 1] + sorted[centerIndex]);
        }
        else {
            return sorted[centerIndex];
        }
    };
    // http://build.fhir.org/ig/HL7/cqf-measures/ValueSet-aggregate-method.html
    MeasureReportBuilder.prototype.aggregate = function (aggregation, observations) {
        // Note: add break for any non-returning case
        switch (aggregation) {
            case Enums_1.AggregationType.SUM:
                // sum	Sum	The measure score is determined by adding together the observations derived from the measure population.
                return observations.reduce(function (accumulator, currentValue) { return accumulator + currentValue; });
            case Enums_1.AggregationType.AVERAGE:
                // average	Average	The measure score is determined by taking the average of the observations derived from the measure population.
                return observations.reduce(function (accumulator, currentValue) { return accumulator + currentValue; }) / observations.length;
            case Enums_1.AggregationType.MEDIAN:
                // median	Median	The measure score is determined by taking the median of the observations derived from the measure population.
                return this.median(observations);
            case Enums_1.AggregationType.MIN:
                // minimum	Minimum	The measure score is determined by taking the minimum of the observations derived from the measure population.
                return Math.min.apply(Math, observations);
            case Enums_1.AggregationType.MAX:
                // maximum	Maximum	The measure score is determined by taking the maximum of the observations derived from the measure population.
                return Math.max.apply(Math, observations);
            case Enums_1.AggregationType.COUNT:
                // count	Count	The measure score is determined as the number of observations derived from the measure population.
                return observations.length;
            default:
                throw new CustomErrors_1.UnsupportedProperty("Measure score aggregation type \"" + aggregation + "\" not supported");
        }
    };
    // CV requires different input types than other scores
    MeasureReportBuilder.prototype.calcMeasureScoreCV = function (measure, detail, groupID, strataCode) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        var observations = [];
        if (detail.episodeResults) {
            // find all episode results with a true measure observation
            var relevantEpisodes = ((_a = detail.episodeResults) === null || _a === void 0 ? void 0 : _a.filter(function (er) {
                var _a, _b, _c;
                // ignore stratification (by setting as true) if stratifier not passed in
                var inStrat = strataCode ? (_b = (_a = er.stratifierResults) === null || _a === void 0 ? void 0 : _a.find(function (sr) { return sr.strataCode == strataCode; })) === null || _b === void 0 ? void 0 : _b.result : true;
                return ((_c = er.populationResults.find(function (pr) { return pr.populationType === Enums_1.PopulationType.OBSERV; })) === null || _c === void 0 ? void 0 : _c.result) && inStrat;
            })) || [];
            // Note: only uses first of potentially multiple observations in each episode (since we can't tell which observation is which)
            // if there are multiple oservations, then this may cause inconsistent behavior
            observations = relevantEpisodes.map(function (er) { var _a; return (_a = er.populationResults.find(function (pr) { return pr.populationType === Enums_1.PopulationType.OBSERV; })) === null || _a === void 0 ? void 0 : _a.observations[0]; });
        }
        else {
            // ignore stratification (by setting as true) if stratifier not passed in
            var inStrat = strataCode ? (_c = (_b = detail.stratifierResults) === null || _b === void 0 ? void 0 : _b.find(function (sr) { return sr.strataCode === strataCode; })) === null || _c === void 0 ? void 0 : _c.result : true;
            // CV for patient-based results is untested
            var obsResultPop = (_d = detail.populationResults) === null || _d === void 0 ? void 0 : _d.find(function (pr) { return pr.populationType === Enums_1.PopulationType.OBSERV; });
            observations = (obsResultPop === null || obsResultPop === void 0 ? void 0 : obsResultPop.result) && inStrat ? [obsResultPop.observations[0]] : [];
        }
        // find aggregation type
        var measureGroup = (_e = measure.group) === null || _e === void 0 ? void 0 : _e.find(function (g) { return g.id === groupID; });
        var observPop = (_f = measureGroup === null || measureGroup === void 0 ? void 0 : measureGroup.population) === null || _f === void 0 ? void 0 : _f.find(function (p) { var _a, _b; return (_b = (_a = p.code) === null || _a === void 0 ? void 0 : _a.coding) === null || _b === void 0 ? void 0 : _b.find(function (c) { return c.code === 'measure-observation'; }); });
        var aggregation = ((_h = (_g = observPop === null || observPop === void 0 ? void 0 : observPop.extension) === null || _g === void 0 ? void 0 : _g.find(function (e) { return e.url === 'http://hl7.org/fhir/us/cqfmeasures/StructureDefinition/cqfm-aggregateMethod'; })) === null || _h === void 0 ? void 0 : _h.valueCode) || '';
        // Note: unit not currently available in data, so not included in this quantity (should be inferable from measure to whoever's using the score)
        // Score also captured in evaluated resource observation
        return {
            value: this.aggregate(aggregation, observations) //|| 0 TODO: set to alternative value if null (no relevant episodes)?
        };
    };
    // TODO: shouldn't this have a group reference or something since the score is specific to a group? (how would this score be re-associated with the right group?)
    MeasureReportBuilder.prototype.addScoreObservation = function (score, measure, report) {
        var _a, _b;
        // create observation resource to reflect the score value and add as reference in evaluated resources
        var observationResource = {
            resourceType: 'Observation',
            code: {
                text: 'MeasureObservation'
            },
            id: uuid_1.v4(),
            status: 'final',
            valueQuantity: score
        };
        // is this extension necessary?
        observationResource.extension = [
            {
                url: 'http://hl7.org/fhir/StructureDefinition/cqf-measureInfo',
                extension: [
                    {
                        url: 'measure',
                        valueCanonical: measure.url
                    },
                    {
                        url: 'populationId',
                        valueString: 'MeasureObservation'
                    }
                ]
            }
        ];
        (_a = report.contained) === null || _a === void 0 ? void 0 : _a.push(observationResource);
        (_b = report.evaluatedResource) === null || _b === void 0 ? void 0 : _b.push({
            reference: "#" + observationResource.id
        });
    };
    MeasureReportBuilder.prototype.calcMeasureScore = function (scoringCode, population) {
        switch (scoringCode) {
            case Enums_1.MeasureScoreType.PROP:
                // (Numerator - Numerator Exclusions) / (Denominator - D Exclusions - D Exceptions).
                var numeratorCount = this.populationTotal(population, Enums_1.PopulationType.NUMER) -
                    this.populationTotal(population, Enums_1.PopulationType.NUMEX);
                var denominatorCount = this.populationTotal(population, Enums_1.PopulationType.DENOM) -
                    this.populationTotal(population, Enums_1.PopulationType.DENEX) -
                    this.populationTotal(population, Enums_1.PopulationType.DENEXCEP);
                return {
                    // TODO: what if value for denominator 0? ... do we need to subtract denex, dexecep... probably, as https://ecqi.healthit.gov/system/files/eCQM-Logic-and-Guidance-2018-0504.pdf
                    value: denominatorCount === 0 ? 0 : (numeratorCount / denominatorCount) * 1.0
                };
            case Enums_1.MeasureScoreType.COHORT:
                // Note: Untested measure score type
                return {
                    value: this.populationTotal(population, Enums_1.PopulationType.IPP) * 1.0
                };
            case Enums_1.MeasureScoreType.RATIO:
                // Note: Untested measure score type
                // (NUMER - NUMEX) / (DENOM - DENEX)`
                var numeratorCount2 = this.populationTotal(population, Enums_1.PopulationType.NUMER) -
                    this.populationTotal(population, Enums_1.PopulationType.NUMEX);
                var denominatorCount2 = this.populationTotal(population, Enums_1.PopulationType.DENOM) -
                    this.populationTotal(population, Enums_1.PopulationType.DENEX);
                return {
                    // TODO: what if value for denominator 0? ... do we need to subtract denex, dexecep... probably, as https://ecqi.healthit.gov/system/files/eCQM-Logic-and-Guidance-2018-0504.pdf
                    value: denominatorCount2 === 0 ? 0 : (numeratorCount2 / denominatorCount2) * 1.0
                };
            default:
                throw new CustomErrors_1.UnsupportedProperty("Measure score type \"" + scoringCode + "\" not supported");
        }
    };
    MeasureReportBuilder.prototype.calculateGroupScores = function () {
        var _this = this;
        var _a;
        if (!this.isIndividual && this.scoringCode === Enums_1.MeasureScoreType.CV) {
            throw new CustomErrors_1.UnsupportedProperty('Aggregate measure reports for continuous variable measures not supported');
        }
        (_a = this.report.group) === null || _a === void 0 ? void 0 : _a.forEach(function (group) {
            var _a;
            if (group.population) {
                if (_this.scoringCode === Enums_1.MeasureScoreType.CV) {
                    //this...
                }
                else {
                    group.measureScore = _this.calcMeasureScore(_this.scoringCode, group.population);
                }
            }
            else {
                throw new CustomErrors_1.UnexpectedProperty("Group " + group.id + " is missing population results.");
            }
            // calculate all stratifiers in group
            (_a = group.stratifier) === null || _a === void 0 ? void 0 : _a.forEach(function (strat) {
                var _a;
                (_a = strat.stratum) === null || _a === void 0 ? void 0 : _a.forEach(function (stratum) {
                    if (stratum.population) {
                        if (_this.scoringCode === Enums_1.MeasureScoreType.CV) {
                            //
                        }
                        else {
                            stratum.measureScore = _this.calcMeasureScore(_this.scoringCode, stratum.population);
                        }
                    }
                    else {
                        throw new CustomErrors_1.UnexpectedProperty("Group " + group.id + " Stratifier " + strat.id + " is missing population results.");
                    }
                });
            });
        });
    };
    MeasureReportBuilder.prototype.getReport = function () {
        this.calculateGroupScores();
        return this.report;
    };
    MeasureReportBuilder.buildMeasureReports = function (measureBundle, executionResults, options) {
        var reports = [];
        executionResults.forEach(function (result) {
            var builder = new MeasureReportBuilder(measureBundle, options);
            builder.addPatientResults(result);
            reports.push(builder.getReport());
        });
        return reports;
    };
    return MeasureReportBuilder;
}());
exports.default = MeasureReportBuilder;
//# sourceMappingURL=MeasureReportBuilder.js.map